import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { CreditCard, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { httpClient } from '../../common/httpclient';

// Detalhe da maquininha — dialog central somente-leitura, DESIGN-SYSTEM.md
// §5.6 "Center modal" (scrim + box 20px + header ícone/título/descrição + X +
// corpo rolável + rodapé com ações à direita), mesmo padrão de
// `pages/services/View.js`, preenchido com o `detail-grid` +
// `rate-summary-grid` de `prototipo-lista-maquininhas.html` (Máx.
// parcelas/Taxa débito/Taxa PIX + matriz completa de taxa por parcela em
// chips).
function DetailItem({ label, value, muted = false, full = false }) {
    return (
        <Box sx={{ gridColumn: full ? '1 / -1' : 'auto' }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '4px' }}>
                {label}
            </Typography>
            {typeof value === 'string' ? (
                <Typography sx={{ fontSize: '13.5px', fontWeight: muted ? 500 : 600, color: muted ? 'text.disabled' : 'text.primary', lineHeight: 1.4 }}>
                    {value}
                </Typography>
            ) : (
                <Box sx={{ mt: '2px' }}>{value}</Box>
            )}
        </Box>
    );
}

// `.rate-chip` de `prototipo-lista-maquininhas.html` — grid de chips
// "1x / 2,20%" para cada parcela da matriz de taxa de crédito.
function RateChip({ installments, tax }) {
    return (
        <Box sx={{
            bgcolor: 'background.subtle',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '9px',
            p: '7px 8px',
            fontSize: '11px',
            textAlign: 'center',
            color: 'text.disabled',
        }}>
            {installments}x
            <Box component="b" sx={{ display: 'block', fontSize: '12.5px', color: 'text.primary', mt: '2px', fontWeight: 700 }}>
                {tax}%
            </Box>
        </Box>
    );
}

export default function CardMachineViewDialog({ open, onClose, cardMachineId, onRequestEdit, canEdit = true }) {
    const [loading, setLoading] = useState(true);
    const [cardMachine, setCardMachine] = useState({});

    const fetchCardMachine = async (id) => {
        try {
            setLoading(true);
            const response = await httpClient.get(`/cardmachines/${id}`);
            setCardMachine(response.data || {});
        } catch (error) {
            console.error('Erro ao carregar dados da maquininha:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (cardMachineId && open) {
            fetchCardMachine(cardMachineId);
        }
    }, [cardMachineId, open]);

    const handleClose = () => {
        setCardMachine({});
        setLoading(true);
        onClose();
    };

    const installmentRates = cardMachine.InstallmentCreditTax || [];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
        >
            <Box sx={{
                px: '22px',
                pt: '20px',
                pb: '14px',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1.25,
            }}>
                <Box>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'info.bg',
                        color: 'primary.main',
                        mb: '10px',
                    }}>
                        <CreditCard size={18} strokeWidth={2} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, mb: '4px' }}>
                        {loading ? 'Carregando…' : (cardMachine.Name || '—')}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                        Detalhes da maquininha cadastrada.
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <X size={15} strokeWidth={2} />
                </IconButton>
            </Box>

            <Box sx={{ p: '18px 22px', maxHeight: '60vh', overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' }}>
                        <DetailItem label="Máx. parcelas" value={`${cardMachine.MaxAmountInstallment ?? '—'}x`} />
                        <DetailItem label="Taxa débito" value={`${cardMachine.TaxDebit ?? '0,00'}%`} />
                        <DetailItem label="Taxa PIX" value={`${cardMachine.TaxPixQrCode ?? '0,00'}%`} />
                        <DetailItem
                            full
                            label="Taxa por parcela (crédito)"
                            value={
                                installmentRates.length > 0 ? (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(66px, 1fr))', gap: '7px', mt: '6px' }}>
                                        {installmentRates
                                            .slice()
                                            .sort((a, b) => a.installment_number - b.installment_number)
                                            .map((rate) => (
                                                <RateChip key={rate.installment_number} installments={rate.installment_number} tax={rate.tax} />
                                            ))}
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontSize: '13.5px', color: 'text.disabled' }}>Nenhuma taxa de parcelamento cadastrada.</Typography>
                                )
                            }
                            muted={installmentRates.length === 0}
                        />
                    </Box>
                )}
            </Box>

            <Box sx={{
                p: '16px 22px 20px',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 1.25,
                justifyContent: 'flex-end',
            }}>
                <Button variant="outlined" onClick={handleClose}>Fechar</Button>
                {canEdit && (
                    <Button
                        variant="contained"
                        onClick={() => {
                            const cm = cardMachine;
                            handleClose();
                            if (onRequestEdit) onRequestEdit(cm);
                        }}
                    >
                        Editar
                    </Button>
                )}
            </Box>
        </Dialog>
    );
}
